// CiroKontrolPaneli — modern unified card.
// Header strip + 3-column body: gauge | metric cards | contributor list.

const ACCENT  = '#3a5af0';
const GREEN   = '#18a558';
const AMBER   = '#e0a106';
const BORDER  = '#e8eaee';
const MUTED   = '#626b78';

export const KP_PALETTE = {
  ACCENT,
  GREEN,
  AMBER,
  BORDER,
  MUTED,
};

export const ciroKontrolPaneliStyles = {
  card: {
    background: '#fff',
    border: `1px solid ${BORDER}`,
    borderRadius: '18px',
    boxShadow: '0 1px 2px rgba(20,30,55,.04)',
    overflow: 'hidden',
  },

  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 1.5,
    px: { xs: 2, md: 3 },
    py: 2.25,
    borderBottom: '1px solid #eef0f4',
  },

  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
  },

  headerIcon: {
    width: 38,
    height: 38,
    borderRadius: '12px',
    background: 'linear-gradient(135deg, rgba(58,90,240,0.20), rgba(58,90,240,0.08))',
    border: '1px solid rgba(58,90,240,0.22)',
    color: ACCENT,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 1px 2px rgba(58,90,240,0.10)',
  },

  headerTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    flexWrap: 'wrap',
  },

  headerTitle: {
    fontSize: '15px',
    fontWeight: 700,
    color: '#0f1424',
    letterSpacing: '-0.2px',
    lineHeight: 1.2,
  },

  independentChip: {
    display: 'inline-flex',
    alignItems: 'center',
    px: 1,
    py: 0.3,
    borderRadius: '999px',
    background: 'rgba(58,90,240,0.10)',
    color: ACCENT,
    border: '1px solid rgba(58,90,240,0.20)',
    fontSize: '10.5px',
    fontWeight: 700,
    letterSpacing: '0.02em',
    lineHeight: 1,
    whiteSpace: 'nowrap',
  },

  headerSubtitle: {
    fontSize: '12.5px',
    color: MUTED,
    mt: 0.25,
    maxWidth: 420,
  },

  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.25,
    flexWrap: 'wrap',
  },

  filterGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
  },

  filterPrefix: {
    fontSize: '11.5px',
    fontWeight: 700,
    color: MUTED,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
  },

  filterControl: {
    minWidth: 170,
    '& .MuiOutlinedInput-root': {
      height: 38,
      borderRadius: '10px',
      background: '#fff',
      '& fieldset': { borderColor: BORDER },
      '&:hover fieldset': { borderColor: '#cbd1da' },
      '&.Mui-focused fieldset': { borderColor: ACCENT },
    },
    '& .MuiInputBase-input': {
      fontSize: '13px',
      fontWeight: 600,
      color: '#0f1424',
      py: 0,
    },
    '& .MuiSelect-icon': {
      color: '#8b95a5',
    },
  },

  filterAdornment: {
    fontSize: '1rem',
    color: '#8b95a5',
    mr: 0.25,
  },

  body: {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', md: '280px 1fr 1.05fr' },
    gap: { xs: 3, md: 3 },
    px: { xs: 2, md: 3 },
    py: 3,
  },

  loadingBox: {
    display: 'flex',
    justifyContent: 'center',
    py: 8,
  },

  errorBox: {
    px: 3,
    py: 4,
    color: '#d64545',
    fontSize: '0.9rem',
    textAlign: 'center',
  },

  /* ---- Sol: Gauge ---- */
  gaugeWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 2,
  },

  gaugeBox: {
    position: 'relative',
    width: 172,
    height: 172,
  },

  gaugeCenter: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
  },

  gaugeValue: {
    fontSize: '32px',
    fontWeight: 800,
    color: '#0f1424',
    letterSpacing: '-1px',
    lineHeight: 1,
    fontVariantNumeric: 'tabular-nums',
  },

  gaugeLabel: {
    fontSize: '11px',
    fontWeight: 700,
    color: MUTED,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    mt: 0.75,
  },

  statusPill: (color) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    px: 1.25,
    py: 0.5,
    borderRadius: '999px',
    background: `${color}1f`,
    color,
    fontSize: '11.5px',
    fontWeight: 700,
  }),

  /* ---- Orta: Metric cards ---- */
  metricsCol: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1.75,
  },

  metricCard: (tone) => ({
    p: 2,
    borderRadius: '14px',
    background: tone === 'success'
      ? 'rgba(24,165,88,0.08)'
      : '#f4f5f7',
    border: tone === 'success'
      ? '1px solid rgba(24,165,88,0.20)'
      : `1px solid ${BORDER}`,
  }),

  metricLabel: {
    fontSize: '11.5px',
    fontWeight: 700,
    color: MUTED,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },

  metricValue: (tone) => ({
    fontSize: '22px',
    fontWeight: 800,
    color: tone === 'success' ? GREEN : '#0f1424',
    letterSpacing: '-0.5px',
    lineHeight: 1.15,
    fontVariantNumeric: 'tabular-nums',
    mt: 0.5,
  }),

  currency: {
    fontSize: '0.65em',
    fontWeight: 700,
    color: MUTED,
    ml: 0.5,
  },

  /* ---- Sağ: Contributors ---- */
  contribCol: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5,
  },

  contribHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1,
  },

  contribTitle: {
    fontSize: '14px',
    fontWeight: 700,
    color: '#0f1424',
    letterSpacing: '-0.1px',
  },

  contribBadge: {
    px: 1,
    py: 0.4,
    borderRadius: '999px',
    background: '#f4f5f7',
    color: MUTED,
    fontSize: '11.5px',
    fontWeight: 700,
  },

  contribRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.75,
  },

  contribTop: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.25,
  },

  contribAvatar: {
    bgcolor: ACCENT,
    color: '#fff',
    width: 32,
    height: 32,
    fontSize: 12,
    fontWeight: 700,
  },

  contribName: {
    flexGrow: 1,
    minWidth: 0,
    fontSize: '13.5px',
    fontWeight: 700,
    color: '#0f1424',
    letterSpacing: '-0.1px',
  },

  contribAmount: {
    fontSize: '13px',
    fontWeight: 700,
    color: '#0f1424',
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
  },

  contribTrack: {
    height: 6,
    borderRadius: '999px',
    background: '#eef0f4',
    overflow: 'hidden',
  },

  contribFill: (pct) => ({
    height: '100%',
    width: `${Math.min(100, Math.max(0, pct))}%`,
    background: `linear-gradient(90deg, ${ACCENT}, #6f8aff)`,
    borderRadius: '999px',
    transition: 'width 0.4s ease',
  }),

  contribPct: {
    fontSize: '11.5px',
    color: MUTED,
    fontWeight: 600,
  },

  contribEmpty: {
    py: 4,
    textAlign: 'center',
    color: '#8b95a5',
    fontSize: '13px',
  },

  contribInfo: {
    mt: 1.5,
    pt: 1.5,
    borderTop: `1px dashed ${BORDER}`,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 1,
    fontSize: '11.5px',
    color: MUTED,
    lineHeight: 1.4,
  },
};
